import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { takedownApi } from "core/api";
import { ReplyCoveredUrl, ReplyEsito, ReplyRecord, ReplyUrlState } from "core/types/takedown.types";
import { categoryMeta } from "./replyCategories";

const fmtDate = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

// Messaggio dell'API se c'è (il backend risponde {error, code, details}), altrimenti quello di rete.
const apiError = (e: any): string => e?.response?.data?.error || e?.message || String(e);

const ESITI: ReplyEsito[] = ["accolta", "respinta", "azione_manuale", "nessuna_azione"];

// Colore del chip d'esito: solo 'accolta' è un successo. 'respinta' non è un errore di sistema —
// è una conversazione conclusa male ma conclusa, e va letta come tale.
const esitoColor = (e: ReplyEsito): "success" | "error" | "warning" | "default" =>
  e === "accolta" ? "success" : e === "respinta" ? "error" : e === "azione_manuale" ? "warning" : "default";

/**
 * Inbox globale risposte legal@codertd.com — GET /admin/takedown/replies (senza practice_id).
 * Complementa la sezione "Azioni richieste" per-pratica già presente in takedown.tsx: qui si
 * vede TUTTO (anche removed_confirmed/ack_ticket/auto_reply), non solo needs_human.
 *
 * Da qui si CHIUDE una conversazione (POST /admin/takedown/reply-close). Prima non si poteva:
 * inbox.py alzava needs_human = 1 e nessuna scrittura lo riportava a 0, quindi le 5 risposte
 * della pratica 280 sono rimaste in coda otto giorni — anche le due in cui il destinatario aveva
 * davvero deindicizzato (umbriaon.it, palermotoday.it via Aruba). Una coda che non cala smette
 * di essere letta, e con lei si perde l'unico posto in cui si vede cosa hanno risposto i siti.
 */
export const RepliesInboxPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ReplyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [onlyNeedsHuman, setOnlyNeedsHuman] = useState(true);

  // Riga in chiusura + scelte dell'operatore. `urlState` vale solo con esito 'accolta': marcare
  // link come rimossi chiudendo una risposta respinta sarebbe una contraddizione che finisce
  // dritta nella rendicontazione al cliente (il backend la rifiuta comunque).
  const [target, setTarget] = useState<ReplyRecord | null>(null);
  const [esito, setEsito] = useState<ReplyEsito>("accolta");
  const [urlState, setUrlState] = useState<ReplyUrlState | "">("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // URL coperti dalla risposta + quelli SCELTI. La dichiarazione può essere PARZIALE: il titolare
  // di palermotoday.it ha messo noindex su 3 pagine su 7, e dichiararle tutte metterebbe 4 link
  // "deindicizzati" che sono ancora perfettamente trovabili. Il default resta "tutti", perché è
  // il caso normale; togliere una spunta è cosa si fa quando la risposta lo dice.
  const [coveredUrls, setCoveredUrls] = useState<ReplyCoveredUrl[]>([]);
  const [chosenUrls, setChosenUrls] = useState<number[]>([]);
  const [urlsLoading, setUrlsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { replies } = await takedownApi.getReplies();
      setRows(Array.isArray(replies) ? replies : []);
    } catch (e: any) {
      setError(t("takedown.replies.error_load", { error: apiError(e) }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (onlyNeedsHuman ? rows.filter((r) => r.needs_human) : rows),
    [rows, onlyNeedsHuman]
  );

  const needsHumanCount = useMemo(() => rows.filter((r) => r.needs_human).length, [rows]);

  const openClose = async (r: ReplyRecord) => {
    setTarget(r);
    setEsito("accolta");
    setUrlState("");
    setNote("");
    setError(null);
    setCoveredUrls([]);
    setChosenUrls([]);
    setUrlsLoading(true);
    try {
      const res = await takedownApi.getReplyUrls(r.id);
      const urls = Array.isArray(res.urls) ? res.urls : [];
      setCoveredUrls(urls);
      setChosenUrls(urls.map((u) => u.url_id));
    } catch (e: any) {
      // Non blocca la chiusura: senza elenco si ricade sul comportamento "tutti gli URL coperti",
      // che è quello che il backend fa già quando url_ids non arriva.
      setCoveredUrls([]);
    } finally {
      setUrlsLoading(false);
    }
  };

  const toggleUrl = (id: number) =>
    setChosenUrls((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Si mandano gli url_ids SOLO quando sono una scelta: se sono tutti, l'assenza del campo e
  // l'elenco completo dicono la stessa cosa, e l'assenza sopravvive a un URL aggiunto all'azione
  // fra l'apertura della finestra e il salvataggio.
  const parziale = !!coveredUrls.length && chosenUrls.length < coveredUrls.length;
  const dichiarazioneVuota = !!urlState && !!coveredUrls.length && chosenUrls.length === 0;

  const submitClose = async () => {
    if (!target) return;
    setSaving(true);
    setError(null);
    try {
      const dichiara = esito === "accolta" && urlState ? urlState : undefined;
      const res = await takedownApi.closeReply(target.id, esito, {
        note: note.trim() || undefined,
        urlState: dichiara,
        urlIds: dichiara && parziale ? chosenUrls : undefined,
      });
      setTarget(null);
      // I fallimenti NON si ingoiano: se lo stato di un URL non è stato scritto, l'operatore
      // crede di aver aggiornato la pratica e invece no.
      if (res.url_falliti?.length) {
        setError(t("takedown.replies.close.partial", { count: res.url_falliti.length }));
      } else if (res.url_dichiarati?.length) {
        setInfo(t("takedown.replies.close.done_urls", { count: res.url_dichiarati.length }));
      } else {
        setInfo(t("takedown.replies.close.done"));
      }
      await load();
    } catch (e: any) {
      setError(t("takedown.replies.close.error", { error: apiError(e) }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ borderRadius: "10px", p: "20px", mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MarkEmailUnreadOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {t("takedown.replies.title")}
          </Typography>
          {needsHumanCount > 0 && (
            <Chip size="small" color="warning" label={t("takedown.replies.needs_review_count", { count: needsHumanCount })} />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Switch size="small" checked={onlyNeedsHuman} onChange={(e) => setOnlyNeedsHuman(e.target.checked)} />}
            label={t("takedown.replies.only_needs_action")}
          />
          <Tooltip title={t("takedown.common.actions.refresh")}>
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} color="secondary" />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 480, mt: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("takedown.replies.table.domain")}</TableCell>
                <TableCell>{t("takedown.replies.table.category")}</TableCell>
                <TableCell>{t("takedown.replies.table.sender")}</TableCell>
                <TableCell>{t("takedown.replies.table.subject")}</TableCell>
                <TableCell>{t("takedown.replies.table.todo")}</TableCell>
                <TableCell>{t("takedown.replies.table.ticket")}</TableCell>
                <TableCell>{t("takedown.replies.table.received")}</TableCell>
                <TableCell>{t("takedown.replies.table.outcome")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const cm = categoryMeta(r.category, t);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.domain || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" color={cm.color} label={cm.label} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.from_addr || "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.subject || "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary">
                        {r.todo}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.ticket_ref || "—"}</TableCell>
                    <TableCell>{fmtDate(r.received_at)}</TableCell>
                    <TableCell>
                      {r.esito ? (
                        <Tooltip title={t("takedown.replies.close.closed_by", { who: r.closed_by || "—", when: fmtDate(r.closed_at) })}>
                          <Chip size="small" color={esitoColor(r.esito)} label={t(`takedown.replies.esito.${r.esito}`)} />
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<TaskAltRoundedIcon fontSize="small" />}
                          onClick={() => openClose(r)}
                        >
                          {t("takedown.replies.close.action")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                      {onlyNeedsHuman ? t("takedown.replies.empty_filtered") : t("takedown.replies.empty_all")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!target} onClose={() => (saving ? null : setTarget(null))} fullWidth maxWidth="sm">
        <DialogTitle>{t("takedown.replies.close.title", { domain: target?.domain || "—" })}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("takedown.replies.close.hint")}</DialogContentText>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="esito-label">{t("takedown.replies.close.esito_label")}</InputLabel>
              <Select
                labelId="esito-label"
                label={t("takedown.replies.close.esito_label")}
                value={esito}
                onChange={(e) => {
                  setEsito(e.target.value as ReplyEsito);
                  if (e.target.value !== "accolta") setUrlState("");
                }}
              >
                {ESITI.map((e) => (
                  <MenuItem key={e} value={e}>
                    {t(`takedown.replies.esito.${e}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {esito === "accolta" && (
              <FormControl fullWidth size="small">
                <InputLabel id="urlstate-label">{t("takedown.replies.close.url_state_label")}</InputLabel>
                <Select
                  labelId="urlstate-label"
                  label={t("takedown.replies.close.url_state_label")}
                  value={urlState}
                  onChange={(e) => setUrlState(e.target.value as ReplyUrlState | "")}
                >
                  <MenuItem value="">{t("takedown.replies.close.url_state_none")}</MenuItem>
                  <MenuItem value="removed">{t("takedown.replies.close.url_state_removed")}</MenuItem>
                  <MenuItem value="deindexed">{t("takedown.replies.close.url_state_deindexed")}</MenuItem>
                </Select>
                {!!urlState && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {t("takedown.replies.close.declaration_warning")}
                  </Typography>
                )}
              </FormControl>
            )}

            {esito === "accolta" && !!urlState && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t("takedown.replies.close.urls_title", {
                    scelti: chosenUrls.length,
                    totale: coveredUrls.length,
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {t("takedown.replies.close.urls_hint")}
                </Typography>
                {urlsLoading ? (
                  <CircularProgress size={18} />
                ) : !coveredUrls.length ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("takedown.replies.close.urls_empty")}
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 200, overflowY: "auto", pr: 1 }}>
                    {coveredUrls.map((u) => (
                      <Stack key={u.url_id} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Checkbox
                          size="small"
                          checked={chosenUrls.includes(u.url_id)}
                          onChange={() => toggleUrl(u.url_id)}
                        />
                        <Tooltip title={u.url || `URL #${u.url_id}`}>
                          <Link
                            href={u.url || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            variant="caption"
                            noWrap
                            sx={{ flex: 1, minWidth: 0, display: "block" }}
                          >
                            {u.url || `URL #${u.url_id}`}
                          </Link>
                        </Tooltip>
                        {!!u.status && <Chip size="small" variant="outlined" label={u.status} />}
                      </Stack>
                    ))}
                  </Box>
                )}
                {dichiarazioneVuota && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t("takedown.replies.close.urls_none_selected")}
                  </Alert>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label={t("takedown.replies.close.note_label")}
              placeholder={t("takedown.replies.close.note_placeholder")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)} disabled={saving}>
            {t("takedown.common.actions.cancel")}
          </Button>
          <Button variant="contained" onClick={submitClose} disabled={saving || dichiarazioneVuota}>
            {saving ? <CircularProgress size={18} /> : t("takedown.replies.close.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RepliesInboxPage;
